/**
 * Legal Page Helper – Wiederverwendbares System für AGB & Datenschutz
 *
 * Features:
 * - Sticky Sidebar mit Inhaltsverzeichnis (Desktop)
 * - Ein-/Ausklappbare Paragraphen
 * - Smooth Scroll zu Sections
 * - Mobile-optimiert
 */

import { isMobileLayout } from '../utils/mobile-layout.js';

/** Section-Datenstruktur für Legal Pages */
export interface LegalSection {
  /** Eindeutige ID für Anker-Navigation (z.B. "paragraph-1") */
  id: string;
  /** Überschrift der Section (z.B. "§ 1 Geltungsbereich") */
  title: string;
  /** HTML-Inhalt der Section */
  content: string;
  /** Optional: Unter-Sections (z.B. "2.1 Registrierung") */
  subsections?: LegalSection[];
  /** Standard: eingeklappt (false) oder ausgeklappt (true) */
  defaultExpanded?: boolean;
}

/**
 * Rendert eine Legal-Page mit Sidebar-Navigation und Collapsible Sections.
 * 
 * @param title - Seitentitel (z.B. "Allgemeine Geschäftsbedingungen")
 * @param meta - Meta-Info (z.B. "Stand: 26.05.2026")
 * @param sections - Array von Sections mit Inhalten
 */
export function renderLegalPage(title: string, meta: string, sections: LegalSection[]): void {
  const app = document.getElementById('app');
  if (!app) return;

  // Inhaltsverzeichnis generieren
  const tocHTML = sections.map(s => {
    const subsHTML = s.subsections 
      ? `<ul class="legal-toc-subsections">
           ${s.subsections.map(sub => 
             `<li><a href="#${sub.id}" class="legal-toc-link legal-toc-link--sub" data-section="${sub.id}">${escapeHtml(sub.title)}</a></li>`
           ).join('')}
         </ul>`
      : '';
    return `
      <li class="legal-toc-item">
        <a href="#${s.id}" class="legal-toc-link" data-section="${s.id}">${escapeHtml(s.title)}</a>
        ${subsHTML}
      </li>
    `;
  }).join('');

  // Sections generieren (Haupt + Subsections)
  const sectionsHTML = sections.map(s => {
    const expanded = s.defaultExpanded ?? false;
    const mainContent = renderSection(s, expanded, false);
    const subsContent = s.subsections 
      ? s.subsections.map(sub => {
          const subExpanded = sub.defaultExpanded ?? false;
          return renderSection(sub, subExpanded, true);
        }).join('')
      : '';
    return mainContent + subsContent;
  }).join('');

  app.innerHTML = `
    <div class="legal-page-container">
      <!-- Sticky Sidebar: Inhaltsverzeichnis -->
      <aside class="legal-sidebar">
        <div class="legal-sidebar-inner">
          <h2 class="legal-toc-title">INHALT</h2>
          <nav class="legal-toc">
            <ul class="legal-toc-list">
              ${tocHTML}
            </ul>
          </nav>
        </div>
      </aside>

      <!-- Content Area: Titel + Sections -->
      <main class="legal-content">
        <div class="legal-back legal-back--top">
          <a href="/">← ZURÜCK ZUR STARTSEITE</a>
        </div>

        <h1 class="legal-title">${escapeHtml(title)}</h1>
        <p class="legal-meta">${meta}</p>
        
        <div class="legal-sections">
          ${sectionsHTML}
        </div>

        <div class="legal-back">
          <a href="/">← ZURÜCK ZUR STARTSEITE</a>
        </div>
      </main>
    </div>
  `;

  // Event-Listener für Navigation und Collapse/Expand
  attachLegalPageListeners();

  // Deep-Link-Unterstützung (z.B. /agb#paragraph-10 vom Registrierungsformular):
  // der Router rendert per history.pushState + manuellem Funktionsaufruf, kein
  // echter Seitenaufruf – der Browser scrollt einen #hash daher NIE von selbst
  // an, und die Ziel-Section ist außerdem standardmäßig eingeklappt.
  openSectionFromHash();
}

function openSectionFromHash(): void {
  const hash = window.location.hash.slice(1);
  if (!hash) return;

  const section = document.getElementById(hash);
  if (!section?.classList.contains('legal-section')) return;

  const toggle = section.querySelector('.legal-section-toggle') as HTMLElement | null;
  if (toggle && section.getAttribute('data-expanded') === 'false') {
    toggle.click();
  }

  section.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Rendert eine einzelne Section mit Collapse/Expand-Funktion.
 */
function renderSection(section: LegalSection, expanded: boolean, isSub: boolean): string {
  const headerClass = isSub ? 'legal-section-header legal-section-header--sub' : 'legal-section-header';
  const expandedClass = expanded ? 'legal-section--expanded' : '';
  const expandedAttr = expanded ? 'true' : 'false';

  return `
    <section class="legal-section ${expandedClass}" id="${section.id}" data-expanded="${expandedAttr}">
      <button class="legal-section-toggle ${headerClass}" aria-expanded="${expandedAttr}" aria-controls="${section.id}-content">
        <span class="legal-section-title">${escapeHtml(section.title)}</span>
        <span class="legal-section-icon" aria-hidden="true">${expanded ? '▼' : '▶'}</span>
      </button>
      <div class="legal-section-content" id="${section.id}-content" style="${expanded ? '' : 'display: none;'}">
        ${section.content}
      </div>
    </section>
  `;
}

/**
 * Sticky-Sidebar per JS simulieren.
 * Nötig weil #app mit overflow-x:hidden einen Scroll-Container erzeugt,
 * der position:sticky in allen Browsern bricht.
 * Im Mobile-Layout deaktiviert (Sidebar ist dort nicht sticky).
 */
function attachStickyBehavior(): void {
  const sidebar = document.querySelector('.legal-page-container .legal-sidebar') as HTMLElement | null;
  const container = document.querySelector('.legal-page-container') as HTMLElement | null;
  if (!sidebar || !container) return;

  const update = (): void => {
    if (isMobileLayout()) {
      sidebar.style.transform = '';
      return;
    }

    const containerTop = container.getBoundingClientRect().top + window.scrollY;
    const scrolled = window.scrollY - containerTop;

    if (scrolled <= 0) {
      sidebar.style.transform = '';
      return;
    }

    const maxScroll = container.offsetHeight - sidebar.offsetHeight;
    sidebar.style.transform = `translateY(${Math.min(scrolled, Math.max(0, maxScroll))}px)`;
  };

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
}

/**
 * Event-Listener für:
 * - Smooth Scroll zu Sections (TOC-Links)
 * - Collapse/Expand Sections (Toggle-Buttons)
 * - Active State im TOC beim Scrollen
 */
function attachLegalPageListeners(): void {
  attachStickyBehavior();

  // 1. TOC-Links: Smooth Scroll + Expand der Ziel-Section
  const tocLinks = document.querySelectorAll('.legal-toc-link');
  tocLinks.forEach(link => {
    link.addEventListener('click', (e: Event) => {
      e.preventDefault();
      const target = link.getAttribute('data-section');
      if (!target) return;

      const section = document.getElementById(target);
      if (!section) return;

      // Section automatisch aufklappen beim Anspringen
      const toggle = section.querySelector('.legal-section-toggle') as HTMLElement | null;
      if (toggle && section.getAttribute('data-expanded') === 'false') {
        toggle.click();
      }

      // Smooth Scroll
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Active State im TOC
      document.querySelectorAll('.legal-toc-link').forEach(l => { l.classList.remove('legal-toc-link--active'); });
      link.classList.add('legal-toc-link--active');
    });
  });

  // 2. Toggle-Buttons: Collapse/Expand Sections
  const toggleButtons = document.querySelectorAll('.legal-section-toggle');
  toggleButtons.forEach(button => {
    button.addEventListener('click', () => {
      const section = button.closest('.legal-section') as HTMLElement | null;
      if (!section) return;

      const contentId = button.getAttribute('aria-controls');
      const content = contentId ? document.getElementById(contentId) : null;
      if (!content) return;

      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      const newExpanded = !isExpanded;

      // Toggle State
      button.setAttribute('aria-expanded', String(newExpanded));
      section.setAttribute('data-expanded', String(newExpanded));
      section.classList.toggle('legal-section--expanded', newExpanded);
      content.style.display = newExpanded ? '' : 'none';

      // Icon ändern
      const icon = button.querySelector('.legal-section-icon');
      if (icon) {
        icon.textContent = newExpanded ? '▼' : '▶';
      }
    });
  });

  // 3. Scroll-Spy: Active State im TOC beim Scrollen (Optional)
  // TODO: Intersection Observer für automatische TOC-Highlight bei Scroll
}

/**
 * HTML-Escape-Funktion (XSS-Schutz für Titel)
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
